import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../api/client";
import type { CatalogCard } from "../api/client";
import { useCatalogAndCollection } from "../api/useCatalog";
import { AppHeader, Breadcrumb } from "../components/AppHeader";
import { CardBrowser } from "../components/CardBrowser";
import { Inspector } from "../components/Inspector";
import {
  DECK_SIZE,
  type DeckEntry,
  type Faction,
  addCopy,
  canAddToDeck,
  defaultDeckName,
  deckStats,
  entriesFromDeck,
  entriesToPayload,
  inferFaction,
  isFaction,
  removeCopy,
  sortedEntries,
  validateDeck,
} from "../logic/deckBuilder";
import { factionFor } from "../theme/factions";
import { E32 } from "../theme/endesga32";
import { primaryBtn, withBtnStyle } from "../theme/styles";
import { inertAttrs, inertOnClick } from "../logic/inert";
import { RARITIES } from "../theme/rarities";
import { pixelTheme, type Theme } from "../theme/pixel";
import { playSfx } from "../logic/sfx";
import { useActiveGameRedirect } from "../logic/useActiveGameRedirect";
import { useGuestRedirect } from "../logic/useGuestRedirect";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";

// Handles /decks/new and /decks/:id/edit. Faction is inferred from
// the first card added.
export function DeckBuilderPage() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const { narrow } = useResponsiveLayout();
  useActiveGameRedirect();
  useGuestRedirect("deck");
  const params = useParams<{ id?: string }>();
  const deckId = params.id;

  const { catalog, catalogById, owned, loading, error: catalogError } =
    useCatalogAndCollection();

  const [user, setUser] = useState<api.User | null>(null);
  const [name, setName] = useState("");
  const [entries, setEntries] = useState<DeckEntry[]>([]);
  const [deckLoaded, setDeckLoaded] = useState(!deckId);

  useEffect(() => {
    void (async () => {
      try {
        const me = await api.getMe();
        setUser(me.user);
      } catch {
        // Header will fall back to a logged-out state — the rest of
        // the page error-handles the same auth failure separately.
      }
    })();
  }, []);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [inspect, setInspect] = useState<CatalogCard | null>(null);
  // Pristine snapshot for the unsaved-changes prompt on back-nav.
  const [pristine, setPristine] = useState<{ name: string; entries: DeckEntry[] } | null>(
    deckId ? null : { name: "", entries: [] },
  );

  useEffect(() => {
    if (!deckId) return;
    if (catalogById.size === 0) return;
    if (deckLoaded) return;
    (async () => {
      try {
        const { deck } = await api.getDeck(deckId);
        const loaded = entriesFromDeck(deck.cards, catalogById);
        setName(deck.name);
        setEntries(loaded);
        setPristine({ name: deck.name, entries: loaded });
        setDeckLoaded(true);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [deckId, catalogById, deckLoaded]);

  const isDirty = useMemo(() => {
    if (!pristine) return false;
    if (name !== pristine.name) return true;
    if (entries.length !== pristine.entries.length) return true;
    const aMap = new Map(pristine.entries.map((e) => [e.card.id, e.count]));
    for (const e of entries) {
      if (aMap.get(e.card.id) !== e.count) return true;
    }
    return false;
  }, [pristine, name, entries]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const deckCounts = useMemo<Record<string, number>>(() => {
    const m: Record<string, number> = {};
    for (const e of entries) m[e.card.id] = e.count;
    return m;
  }, [entries]);

  const faction = useMemo(() => inferFaction(entries), [entries]);

  // Single add handler for both CardBrowser "+" and Inspector "Add to Deck".
  // Seeds the deck name on first add as a one-shot (doesn't overwrite
  // a name the user later cleared).
  const addOneToDeck = useCallback(
    (c: CatalogCard) => {
      // `addCopy` returns the same reference on rejection (over cap /
      // beyond owned). Use that as the success signal.
      setEntries((prev) => {
        const next = addCopy(prev, c, owned);
        playSfx(next === prev ? "ai_notice_or_warning_sound" : "ai_paper_flutter");
        return next;
      });
      if (!name.trim() && isFaction(c.faction)) {
        setName(defaultDeckName(c.faction));
      }
    },
    [owned, name],
  );

  const validation = validateDeck({ name, faction, entries, owned });
  const total = deckStats(entries).total;

  async function onSave() {
    if (!validation.ok || saving) return;
    setSaving(true);
    setSaveError(null);
    setServerIssues([]);
    try {
      const payload = {
        name,
        faction: faction as Faction,
        cards: entriesToPayload(entries),
      };
      if (deckId) {
        await api.updateDeck(deckId, payload);
      } else {
        await api.createDeck(payload);
      }
      // Avoid the unsaved-changes prompt on the upcoming navigate().
      setPristine({ name, entries });
      navigate("/lobby");
    } catch (err) {
      playSfx("ai_error_sound");
      if (
        err instanceof api.ApiError &&
        err.status === 409 &&
        err.body &&
        typeof err.body === "object" &&
        "error" in err.body &&
        (err.body as { error: unknown }).error === "no_deck_slots"
      ) {
        setSaveError(
          "Out of deck slots — buy a Deck Box from the Cardmaster to make room.",
        );
      } else if (err instanceof api.ApiError && err.status === 409) {
        setSaveError("You have a game in progress — end it before saving.");
      } else if (
        err instanceof api.ApiError &&
        err.status === 422 &&
        err.body &&
        typeof err.body === "object" &&
        "errors" in err.body
      ) {
        setServerIssues(formatServerErrors((err.body as { errors: unknown }).errors));
      } else {
        setSaveError(err instanceof Error ? err.message : String(err));
      }
      setSaving(false);
    }
  }

  const showingLoad = deckId ? !deckLoaded : false;

  return (
    <div
      style={{
        height: "100%",
        overflow: "hidden",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <AppHeader
        user={user}
        onBack={() => {
          if (
            isDirty &&
            !window.confirm("Discard unsaved changes to this deck?")
          ) {
            return;
          }
          if (window.history.length > 1) navigate(-1);
          else navigate("/lobby");
        }}
      />
      <Breadcrumb
        text={`${deckId ? "EDIT DECK" : "NEW DECK"}${
          faction ? ` · ${factionFor(faction).name}` : ""
        }${name ? ` · ${name}` : ""}`}
      />

      {(catalogError || loadError || saveError) && (
        <div
          style={{
            padding: 12,
            background: theme.panel,
            border: `2px solid ${theme.accent}`,
            color: theme.accent,
          }}
        >
          {catalogError || loadError || saveError}
        </div>
      )}

      {serverIssues.length > 0 && (
        <div
          style={{
            padding: 12,
            background: theme.panel,
            border: `2px solid ${theme.accent}`,
            color: theme.accent,
          }}
        >
          <strong>Server rejected the deck:</strong>
          <ul style={{ margin: "6px 0 0 18px" }}>
            {serverIssues.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      {loading || showingLoad ? (
        <div style={{ color: theme.inkFaint }}>Loading…</div>
      ) : narrow ? (
        <div
          style={{
            display: "grid",
            gridTemplateRows: "auto minmax(0, 1fr)",
            gap: 16,
            flex: 1,
            minHeight: 0,
          }}
        >
          <DeckPanel
            theme={theme}
            name={name}
            setName={setName}
            faction={faction}
            entries={entries}
            removeOne={(id) => setEntries((p) => removeCopy(p, id))}
            onInspect={setInspect}
            total={total}
            validation={validation}
            onSave={onSave}
            saving={saving}
            mode={deckId ? "edit" : "new"}
            compact
          />
          <div
            style={{
              background: theme.panel,
              border: `2px solid ${theme.panelEdge}`,
              padding: 12,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardBrowser
              cards={catalog}
              owned={owned}
              theme={theme}
              ownedOnly
              deckCounts={deckCounts}
              deckFaction={faction || undefined}
              onAddCopy={addOneToDeck}
              onInspect={setInspect}
              availableFactions={user?.unlocked_factions}
            />
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) 360px",
            gap: 16,
            flex: 1,
            minHeight: 0,
          }}
        >
          <div
            style={{
              background: theme.panel,
              border: `2px solid ${theme.panelEdge}`,
              padding: 12,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardBrowser
              cards={catalog}
              owned={owned}
              theme={theme}
              ownedOnly
              deckCounts={deckCounts}
              deckFaction={faction || undefined}
              onAddCopy={addOneToDeck}
              onInspect={setInspect}
              availableFactions={user?.unlocked_factions}
            />
          </div>

          <DeckPanel
            theme={theme}
            name={name}
            setName={setName}
            faction={faction}
            entries={entries}
            removeOne={(id) => setEntries((p) => removeCopy(p, id))}
            onInspect={setInspect}
            total={total}
            validation={validation}
            onSave={onSave}
            saving={saving}
            mode={deckId ? "edit" : "new"}
          />
        </div>
      )}

      <Inspector
        card={inspect}
        theme={theme}
        onClose={() => setInspect(null)}
        addAction={inspectorAddAction(
          inspect,
          faction,
          deckCounts,
          owned,
          addOneToDeck,
        )}
      />
    </div>
  );
}

function DeckPanel({
  theme,
  name,
  setName,
  faction,
  entries,
  removeOne,
  onInspect,
  total,
  validation,
  onSave,
  saving,
  mode,
  compact = false,
}: {
  theme: Theme;
  name: string;
  setName: (v: string) => void;
  faction: Faction | "";
  entries: DeckEntry[];
  removeOne: (cardId: string) => void;
  onInspect: (card: CatalogCard) => void;
  total: number;
  validation: ReturnType<typeof validateDeck>;
  onSave: () => void;
  saving: boolean;
  mode: "new" | "edit";
  compact?: boolean;
}) {
  const sorted = sortedEntries(entries);
  const factionLabel = faction ? factionFor(faction).name : "No faction yet";

  if (compact) {
    return (
      <aside
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdge}`,
          padding: 10,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1 1 160px", minWidth: 140 }}>
            <NameInput value={name} onChange={setName} theme={theme} />
          </div>
          <span
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 10,
              letterSpacing: theme.letterSpacing,
              color: faction ? theme.accent : theme.inkFaint,
            }}
          >
            {factionLabel.toUpperCase()}
          </span>
          <span style={{ color: theme.inkFaint, fontSize: 13 }}>
            {total}/{DECK_SIZE}
          </span>
          <button
            type="button"
            onClick={inertOnClick(!validation.ok || saving, onSave)}
            {...inertAttrs(!validation.ok || saving)}
            {...withBtnStyle(primaryBtn(theme, !validation.ok || saving), {
              fontSize: 11,
              padding: "8px 12px",
            })}
          >
            {saving ? "SAVING…" : mode === "edit" ? "SAVE" : "CREATE"}
          </button>
        </div>
        {sorted.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 4,
              maxHeight: 120,
              overflowY: "auto",
            }}
          >
            {sorted.map((e) => (
              <CompactDeckChip
                key={e.card.id}
                entry={e}
                theme={theme}
                onRemove={() => removeOne(e.card.id)}
                onInspect={() => onInspect(e.card)}
              />
            ))}
          </div>
        )}
        {!validation.ok && (
          <ul
            style={{
              margin: 0,
              padding: "6px 10px 6px 26px",
              color: theme.inkDim,
              fontSize: 12,
              background: theme.panelSolid,
              border: `1px dashed ${theme.panelEdge}`,
              maxHeight: 80,
              overflowY: "auto",
            }}
          >
            {validation.issues.map((i, idx) => (
              <li key={idx}>{i.message}</li>
            ))}
          </ul>
        )}
      </aside>
    );
  }

  return (
    <aside
      style={{
        background: theme.panel,
        border: `2px solid ${theme.panelEdge}`,
        padding: 14,
        display: "grid",
        gridTemplateRows: "auto auto minmax(0, 1fr) auto",
        gap: 10,
        minHeight: 0,
      }}
    >
      <NameInput value={name} onChange={setName} theme={theme} />

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            letterSpacing: theme.letterSpacing,
            color: faction ? theme.accent : theme.inkFaint,
          }}
        >
          {factionLabel.toUpperCase()}
        </span>
        <span style={{ marginLeft: "auto", color: theme.inkFaint, fontSize: 13 }}>
          {total}/{DECK_SIZE}
        </span>
      </div>

      <div style={{ overflowY: "auto", minHeight: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        {sorted.length === 0 ? (
          <div style={{ color: theme.inkFaint, padding: "24px 8px", textAlign: "center" }}>
            Click cards on the left to inspect, then tap + to add.
          </div>
        ) : (
          sorted.map((e) => (
            <DeckRow
              key={e.card.id}
              entry={e}
              theme={theme}
              onRemove={() => removeOne(e.card.id)}
              onInspect={() => onInspect(e.card)}
            />
          ))
        )}
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {!validation.ok && (
          <ul
            style={{
              margin: 0,
              padding: "8px 10px 8px 26px",
              color: theme.inkDim,
              fontSize: 13,
              background: theme.panelSolid,
              border: `1px dashed ${theme.panelEdge}`,
              maxHeight: 120,
              overflowY: "auto",
            }}
          >
            {validation.issues.map((i, idx) => (
              <li key={idx}>{i.message}</li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={inertOnClick(!validation.ok || saving, onSave)}
          {...inertAttrs(!validation.ok || saving)}
          {...withBtnStyle(primaryBtn(theme, !validation.ok || saving), {
            fontSize: 12,
            padding: "12px 18px",
          })}
        >
          {saving ? "SAVING…" : mode === "edit" ? "SAVE CHANGES" : "CREATE DECK"}
        </button>
      </div>
    </aside>
  );
}

function CompactDeckChip({
  entry,
  theme,
  onRemove,
  onInspect,
}: {
  entry: DeckEntry;
  theme: Theme;
  onRemove: () => void;
  onInspect: () => void;
}) {
  const f = factionFor(entry.card.faction);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: theme.panelSolid,
        border: `1px solid ${theme.panelEdge}`,
        padding: "2px 4px",
        fontSize: 12,
      }}
    >
      <span
        title={f.name}
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          color: E32.ink,
          background: f.color,
          padding: "1px 4px",
        }}
      >
        {entry.card.cost}
      </span>
      <button
        onClick={onInspect}
        title="Inspect"
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          color: theme.ink,
          cursor: "pointer",
          fontSize: 12,
          maxWidth: 120,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {entry.card.name}
      </button>
      <span style={{ color: theme.inkDim, fontSize: 11 }}>×{entry.count}</span>
      <button
        onClick={onRemove}
        title="Remove one"
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 9,
          padding: "0 4px",
          background: theme.panel,
          color: theme.inkDim,
          border: `1px solid ${theme.panelEdge}`,
          cursor: "pointer",
        }}
      >
        −
      </button>
    </span>
  );
}

function NameInput({
  value,
  onChange,
  theme,
}: {
  value: string;
  onChange: (v: string) => void;
  theme: Theme;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          letterSpacing: theme.letterSpacing,
          color: theme.inkFaint,
        }}
      >
        DECK NAME
      </label>
      <input
        type="text"
        value={value}
        maxLength={200}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. Antar Vanguard"
        className="doi-input"
        style={{
          padding: "8px 10px",
          fontFamily: theme.fontBody,
          fontSize: 16,
          background: theme.panelSolid,
          color: theme.ink,
          border: `2px solid ${theme.panelEdge}`,
        }}
      />
    </div>
  );
}

// Narrow colored spine on the compact deck row, sourced from rarities.ts.
const RARITY_BORDER: Record<string, string> = {
  common: RARITIES.common.color,
  uncommon: RARITIES.uncommon.color,
  rare: RARITIES.rare.color,
  legendary: RARITIES.legendary.color,
};

const ROW_HEIGHT = 38;

function DeckRow({
  entry,
  theme,
  onRemove,
  onInspect,
}: {
  entry: DeckEntry;
  theme: Theme;
  onRemove: () => void;
  onInspect: () => void;
}) {
  const f = factionFor(entry.card.faction);
  const rarityColor = RARITY_BORDER[entry.card.rarity] ?? RARITY_BORDER.common;
  const isLegendary = entry.card.rarity === "legendary";
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto auto",
        alignItems: "center",
        gap: 8,
        padding: "0 8px",
        height: ROW_HEIGHT,
        flexShrink: 0,
        background: theme.panelSolid,
        border: `1px solid ${theme.panelEdge}`,
        borderLeft: `4px solid ${rarityColor}`,
      }}
    >
      <div
        style={{
          minWidth: 22,
          height: 22,
          display: "grid",
          placeItems: "center",
          fontFamily: theme.fontDisplay,
          fontSize: 11,
          color: E32.ink,
          background: f.color,
          flexShrink: 0,
        }}
        title={f.name}
      >
        {entry.card.cost}
      </div>
      <button
        onClick={onInspect}
        title="Inspect"
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          textAlign: "left",
          cursor: "pointer",
          color: theme.ink,
          fontFamily: theme.fontBody,
          fontSize: 15,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {isLegendary && (
          <span
            aria-label="Legendary"
            title="Legendary"
            style={{ color: rarityColor, fontSize: 13, lineHeight: 1 }}
          >
            ★
          </span>
        )}
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {entry.card.name}
        </span>
      </button>
      <span
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 12,
          color: theme.inkDim,
        }}
      >
        ×{entry.count}
      </span>
      <button
        onClick={onRemove}
        title="Remove one"
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          padding: "2px 6px",
          background: theme.panel,
          color: theme.inkDim,
          border: `1px solid ${theme.panelEdge}`,
          cursor: "pointer",
        }}
      >
        −
      </button>
    </div>
  );
}

// Always returns an action (disabled + reason when not addable) so
// the button doesn't vanish when a card can't be added.
function inspectorAddAction(
  card: CatalogCard | null,
  faction: Faction | "",
  deckCounts: Record<string, number>,
  owned: Record<string, number>,
  addOneToDeck: (c: CatalogCard) => void,
): { canAdd: boolean; reason?: string; onAdd: () => void } | undefined {
  if (!card) return undefined;
  const { ok, reason } = canAddToDeck({
    card,
    faction,
    deckCount: deckCounts[card.id] ?? 0,
    owned: owned[card.id] ?? 0,
  });
  return { canAdd: ok, reason, onAdd: () => addOneToDeck(card) };
}

// ---- server-error rendering ----

interface ServerError {
  kind: string;
  [field: string]: unknown;
}

function formatServerErrors(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [String(raw)];
  return raw.map(formatOne);
}

function formatOne(e: unknown): string {
  if (!e || typeof e !== "object") return String(e);
  const err = e as ServerError;
  switch (err.kind) {
    case "invalid_faction":
      return `Invalid faction: ${err.faction}`;
    case "wrong_deck_size":
      return `Deck has ${err.have}/${err.need} cards`;
    case "unknown_cards":
      return `Unknown cards: ${(err.ids as string[]).join(", ")}`;
    case "off_faction_cards":
      return `Off-faction cards: ${(err.ids as string[]).join(", ")}`;
    case "over_copy_cap":
      return (
        "Over copy cap: " +
        (err.entries as { card_id: string; have: number; cap: number }[])
          .map((x) => `${x.card_id} (${x.have}>${x.cap})`)
          .join(", ")
      );
    case "insufficient_copies":
      return (
        "Don't own enough: " +
        (err.entries as { card_id: string; want: number; owned: number }[])
          .map((x) => `${x.card_id} (want ${x.want}, own ${x.owned})`)
          .join(", ")
      );
    default:
      return JSON.stringify(err);
  }
}
